import { useGetChannelSettlementHistoryApiV1Suspense } from "@/api/__generated__/api.schema";
import type { PayoutAmountByChannel } from "@/api/__generated__/models";
import { Profile } from "@/components/profile";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { formatCurrency } from "@/utils/format";
import {
  Collection,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  DropdownTriggerButton,
  Icon,
  MonthPicker,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Typography,
} from "@backpac/monica";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { Temporal } from "temporal-polyfill";

const MAX_MONTH_YYYY_MM = Temporal.Now.plainDateISO().subtract({ months: 1 });

export function useDate() {
  const [searchParams, setSearchParams] = useSearchParams();

  const today = Temporal.Now.plainDateISO().subtract({ months: 1 });
  const yyyyMM = `${today.year}-${today.month.toString().padStart(2, "0")}`;
  const date = searchParams.get("date") ?? yyyyMM;

  return {
    date,
    originalDate: Temporal.PlainDate.from(`${date}-01`),
    setDate: (originalDate: string | undefined) => {
      const newParams = new URLSearchParams(searchParams);
      const date = originalDate
        ? Temporal.PlainDate.from(originalDate).toString().split("-").slice(0, 2).join("-")
        : yyyyMM;
      newParams.set("date", date);
      setSearchParams(newParams);
    },
  };
}

export function PayoutRevenueContents() {
  const workspaceId = useWorkspaceId();
  const [open, setOpen] = useState(false);
  const { date, originalDate, setDate } = useDate();
  const {
    data: {
      data: { items },
    },
  } = useGetChannelSettlementHistoryApiV1Suspense(workspaceId, {
    date,
  });

  return (
    <Collection
      data={items}
      filter={{
        keys: ["channelName", "channelCustomUrl"],
      }}
    >
      <Table
        border={false}
        head={
          <div className="flex flex-col gap-4 px-6 mb-7">
            <div className="flex items-baseline justify-between ">
              <Typography size="lg" color="black" font="unbounded">
                Revenue by channel
              </Typography>
              <Dropdown open={open} onOpenChange={setOpen}>
                <DropdownTrigger asChild>
                  <DropdownTriggerButton>
                    {`${originalDate.toLocaleString("en-US", { month: "long" })}, ${originalDate.year}`}
                  </DropdownTriggerButton>
                </DropdownTrigger>
                <DropdownContent>
                  <MonthPicker
                    maxMonth={MAX_MONTH_YYYY_MM}
                    selectedMonth={originalDate}
                    onMonthSelected={(value) => {
                      setDate(value?.toString());
                      setOpen(false);
                    }}
                  />
                </DropdownContent>
              </Dropdown>
            </div>
            <Collection.SearchInput placeholder="Search channel" className="w-fit" />
          </div>
        }
      >
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-4 desktop:pl-6">Channel Name</TableHead>
            <TableHead className="pr-4 desktop:pr-6 text-right">Amount</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          <Collection.Result<PayoutAmountByChannel>>
            {({ collections }) =>
              collections.length === 0 ? (
                <PayoutRevenueContents.Empty />
              ) : (
                collections.map((item: PayoutAmountByChannel) => (
                  <TableRow key={item.channelId}>
                    <TableCell className="pl-4 desktop:pl-6 py-3 max-w-[360px]">
                      <Profile
                        name={item.channelName}
                        description={item.channelCustomUrl}
                        profileImageUrl={item.channelThumbnailUrl}
                      />
                    </TableCell>
                    <TableCell className="text-right font-semibold pr-4 desktop:pr-6">
                      {formatCurrency(item.amount)}
                    </TableCell>
                  </TableRow>
                ))
              )
            }
          </Collection.Result>
        </TableBody>
      </Table>
    </Collection>
  );
}

PayoutRevenueContents.Empty = function ChannelTableEmpty() {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={5} className="text-center">
        <div className="space-y-3 py-12 text-white">
          <Icon name="dollar-circle" bgColor="brandSkyblue" iconSize="24px" className="size-14" />
          <div className="space-y-2">
            <Typography size="md" color="textPrimary" weight={600}>
              No revenue yet
            </Typography>
            <Typography size="xs" color="textMuted">
              Revenue by channels will appear here
            </Typography>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};
